import * as THREE from "three";

interface ActiveProjectile {
  id: string;
  mesh: THREE.Object3D;
  trail: THREE.Mesh;
  ttl: number;
  weaponType: string;
  /** Rockets only: seconds until the next smoke puff is emitted. */
  puffTimer: number;
}

interface SmokePuff {
  mesh: THREE.Mesh;
  mat: THREE.MeshBasicMaterial;
  ttl: number;
  initialTtl: number;
}

interface BurstPiece {
  mesh: THREE.Mesh;
  vx: number; vy: number; vz: number;
  rvx: number; rvy: number; rvz: number;
}

interface ActiveBurst {
  group: THREE.Group;
  ttl: number;
  initialTtl: number;
  mat: THREE.MeshBasicMaterial;
  pieces: BurstPiece[];
}

interface ActiveMuzzle {
  mesh: THREE.Mesh;
  ttl: number;
  initialTtl: number;
}

interface ActiveSphere {
  mesh: THREE.Mesh;
  mat: THREE.MeshBasicMaterial;
  ttl: number;
  initialTtl: number;
  targetScale: number;
}

interface ActiveRing {
  mesh: THREE.Mesh;
  mat: THREE.MeshBasicMaterial;
  ttl: number;
  initialTtl: number;
  targetScale: number;
}

// Shared geometry instances — created once, never disposed.
const _MUZZLE_GEO   = new THREE.SphereGeometry(0.5, 8, 6);
const _BURST_GEO    = new THREE.BoxGeometry(0.2, 0.2, 0.2);
const _FIREBALL_GEO = new THREE.SphereGeometry(1, 12, 8);   // unit sphere, scaled per-effect
const _RING_GEO     = new THREE.RingGeometry(0.82, 1.0, 32); // thin flat ring, scaled per-effect
// Rocket body parts (shared geometry, per-rocket group).
const _ROCKET_BODY_GEO = new THREE.CylinderGeometry(0.16, 0.16, 1.0, 8);
const _ROCKET_NOSE_GEO = new THREE.ConeGeometry(0.16, 0.4, 8);
const _ROCKET_FIN_GEO  = new THREE.BoxGeometry(0.5, 0.04, 0.22);
const _ROCKET_GLOW_GEO = new THREE.SphereGeometry(0.22, 8, 6);
const _SMOKE_GEO       = new THREE.SphereGeometry(0.28, 6, 5);
const _ROCKET_BODY_MAT = new THREE.MeshBasicMaterial({ color: 0xd8d8e4 });
const _ROCKET_NOSE_MAT = new THREE.MeshBasicMaterial({ color: 0xef4444 });
const _ROCKET_GLOW_MAT = new THREE.MeshBasicMaterial({ color: 0xffb347, transparent: true, opacity: 0.95 });
/** Seconds between smoke puffs per rocket. */
const _PUFF_INTERVAL = 0.03;
const _PUFF_LIFETIME = 0.45;

// Shared material used only as a safe placeholder on pooled burst meshes.
const _PROJ_MAT = new THREE.MeshBasicMaterial({ color: 0x22d3ee });

interface WeaponVisuals {
  mat: THREE.MeshBasicMaterial;
  geo: THREE.SphereGeometry;
  trailMat: THREE.MeshBasicMaterial;
  trailGeo: THREE.CylinderGeometry;
}

const WEAPON_SPECS: Record<string, { radius: number; color: number; trailColor: number; trailRadiusTop: number; trailRadiusBottom: number; trailHeight: number }> = {
  "standard-plasma": { radius: 0.28, color: 0x22d3ee, trailColor: 0xa855f7, trailRadiusTop: 0.04, trailRadiusBottom: 0.14, trailHeight: 1.4 },
  "rapid-blaster":   { radius: 0.18, color: 0xff4444, trailColor: 0xff8800, trailRadiusTop: 0.03, trailRadiusBottom: 0.09, trailHeight: 1.0 },
  "heavy-cannon":    { radius: 0.42, color: 0xff8800, trailColor: 0xffcc00, trailRadiusTop: 0.06, trailRadiusBottom: 0.20, trailHeight: 1.8 },
  "long-range-rail": { radius: 0.20, color: 0xffffff, trailColor: 0x88ccff, trailRadiusTop: 0.02, trailRadiusBottom: 0.07, trailHeight: 2.0 },
  "rocket-launcher": { radius: 0.55, color: 0xff3300, trailColor: 0xff6600, trailRadiusTop: 0.07, trailRadiusBottom: 0.22, trailHeight: 2.2 },
  // Pickup weapons — distinct silhouettes.
  "ricochet":        { radius: 0.26, color: 0x4ade80, trailColor: 0x22c55e, trailRadiusTop: 0.05, trailRadiusBottom: 0.16, trailHeight: 1.6 },
  "homing-rocket":   { radius: 0.30, color: 0xffb347, trailColor: 0x9ca3af, trailRadiusTop: 0.05, trailRadiusBottom: 0.18, trailHeight: 1.8 },
};

/** Per-weapon muzzle flash colour (falls back to the classic yellow). */
export const MUZZLE_COLORS: Record<string, number> = {
  "standard-plasma": 0xfde047,
  "rapid-blaster":   0xff6644,
  "heavy-cannon":    0xffaa22,
  "long-range-rail": 0xbfe3ff,
  "rocket-launcher": 0xff7733,
  "ricochet":        0x4ade80,
  "homing-rocket":   0xffb347,
  "shockwave":       0x60a5fa,
};
const DEFAULT_WEAPON_SPEC = WEAPON_SPECS["standard-plasma"];

const _weaponVisuals = new Map<string, WeaponVisuals>();

function getWeaponVisuals(weaponType: string): WeaponVisuals {
  let v = _weaponVisuals.get(weaponType);
  if (!v) {
    const spec = WEAPON_SPECS[weaponType] ?? DEFAULT_WEAPON_SPEC;
    v = {
      mat:      new THREE.MeshBasicMaterial({ color: spec.color }),
      geo:      new THREE.SphereGeometry(spec.radius, 10, 8),
      trailMat: new THREE.MeshBasicMaterial({ color: spec.trailColor, transparent: true, opacity: 0.6 }),
      trailGeo: new THREE.CylinderGeometry(spec.trailRadiusTop, spec.trailRadiusBottom, spec.trailHeight, 8),
    };
    _weaponVisuals.set(weaponType, v);
  }
  return v;
}

export class EffectsSystem {
  group = new THREE.Group();
  private projectiles   = new Map<string, ActiveProjectile>();
  private bursts:       ActiveBurst[]  = [];
  private muzzleFlashes: ActiveMuzzle[] = [];
  private spheres:      ActiveSphere[] = [];
  private rings:        ActiveRing[]   = [];
  private burstMeshPool: THREE.Mesh[]  = [];
  // Rocket smoke trail — fully pooled (mesh + material pairs, zero
  // steady-state allocations once the pool is warm).
  private smokePuffs: SmokePuff[] = [];
  private smokePool:  Array<{ mesh: THREE.Mesh; mat: THREE.MeshBasicMaterial }> = [];
  /** Pooled rocket body groups — built once, reused across rockets. */
  private rocketPool: THREE.Group[] = [];

  private buildRocketBody(): THREE.Group {
    const g = new THREE.Group();
    const inner = new THREE.Group(); // cone axis +Y → rotate to +Z inside
    const body = new THREE.Mesh(_ROCKET_BODY_GEO, _ROCKET_BODY_MAT);
    const nose = new THREE.Mesh(_ROCKET_NOSE_GEO, _ROCKET_NOSE_MAT);
    nose.position.y = 0.7;
    const finA = new THREE.Mesh(_ROCKET_FIN_GEO, _ROCKET_BODY_MAT);
    finA.position.y = -0.4;
    const finB = new THREE.Mesh(_ROCKET_FIN_GEO, _ROCKET_BODY_MAT);
    finB.position.y = -0.4;
    finB.rotation.y = Math.PI / 2;
    const glow = new THREE.Mesh(_ROCKET_GLOW_GEO, _ROCKET_GLOW_MAT);
    glow.position.y = -0.62;
    inner.add(body, nose, finA, finB, glow);
    inner.rotation.x = Math.PI / 2; // +Y (nose) now points along +Z
    g.add(inner);
    return g;
  }

  upsertProjectile(id: string, x: number, y: number, z: number, weaponType = "", dirX = 0, dirZ = 1): void {
    let p = this.projectiles.get(id);
    if (!p) {
      let mesh: THREE.Object3D;
      const v = getWeaponVisuals(weaponType);
      if (weaponType === "homing-rocket") {
        mesh = this.rocketPool.pop() ?? this.buildRocketBody();
      } else {
        mesh = new THREE.Mesh(v.geo, v.mat);
      }
      const trail = new THREE.Mesh(v.trailGeo, v.trailMat);
      this.group.add(mesh, trail);
      p = { id, mesh, trail, ttl: 4, weaponType, puffTimer: 0 };
      this.projectiles.set(id, p);
    }
    p.mesh.position.set(x, y, z);
    p.trail.position.set(x, y, z);
    if (p.weaponType === "homing-rocket") {
      p.mesh.rotation.y = Math.atan2(dirX, dirZ);
      // Exhaust trail sits behind the rocket, aligned with flight direction.
      p.trail.position.set(x - dirX * 1.1, y, z - dirZ * 1.1);
      p.trail.rotation.set(Math.PI / 2, Math.atan2(dirX, dirZ), 0, "YXZ");
      // Smoke ribbon — pooled puffs dropped along the flight path.
      if (p.puffTimer <= 0) {
        p.puffTimer = _PUFF_INTERVAL;
        this.spawnSmokePuff(x - dirX * 0.9, y, z - dirZ * 0.9);
      }
    }
    p.ttl = 4;
  }

  removeProjectile(id: string): void {
    const p = this.projectiles.get(id);
    if (!p) return;
    this.group.remove(p.mesh, p.trail);
    if (p.weaponType === "homing-rocket" && p.mesh instanceof THREE.Group) {
      this.rocketPool.push(p.mesh);
    }
    this.projectiles.delete(id);
  }

  private spawnSmokePuff(x: number, y: number, z: number): void {
    let entry = this.smokePool.pop();
    if (!entry) {
      const mat = new THREE.MeshBasicMaterial({ color: 0xb9bec7, transparent: true, opacity: 0.5 });
      entry = { mesh: new THREE.Mesh(_SMOKE_GEO, mat), mat };
    }
    entry.mesh.position.set(x, y, z);
    entry.mesh.scale.setScalar(0.6);
    entry.mat.opacity = 0.5;
    this.group.add(entry.mesh);
    this.smokePuffs.push({ mesh: entry.mesh, mat: entry.mat, ttl: _PUFF_LIFETIME, initialTtl: _PUFF_LIFETIME });
  }

  /** Shockwave pulse — stacked expanding rings + a bright core pop. */
  spawnShockwave(x: number, y: number, z: number): void {
    this._spawnSphere(x, y + 0.4, z, 0x60a5fa, 3.0, 0.16);
    this._spawnRing(x, y + 0.25, z, 0x60a5fa, 14, 0.34);
    this._spawnRing(x, y + 0.55, z, 0xffffff, 10, 0.26);
    this._spawnRing(x, y + 0.9,  z, 0x60a5fa, 6, 0.20);
    this.spawnBurst(x, y + 0.4, z, 26, 0x93c5fd, 0.5);
  }

  /** Ricochet bounce — short green spark snap at the impact point. */
  spawnBounce(x: number, y: number, z: number): void {
    this._spawnSphere(x, y, z, 0x4ade80, 0.9, 0.08);
    this.spawnBurst(x, y, z, 10, 0x4ade80, 0.3);
  }

  /** Rocket launch backblast at the muzzle. */
  spawnRocketLaunch(x: number, y: number, z: number): void {
    this._spawnSphere(x, y, z, 0xffb347, 1.6, 0.14);
    this.spawnBurst(x, y, z, 12, 0x9ca3af, 0.5);
  }

  spawnHit(x: number, y: number, z: number, color = 0xfb923c): void {
    // Small white pop at impact point
    this._spawnSphere(x, y, z, 0xffffff, 1.0, 0.06);
    // More sparks than before (20 vs 12)
    this.spawnBurst(x, y, z, 20, color, 0.6);
  }

  // Projectile reached max range — small puff so players can read weapon range visually
  spawnFizzle(x: number, y: number, z: number, weaponType = ""): void {
    const spec = WEAPON_SPECS[weaponType] ?? DEFAULT_WEAPON_SPEC;
    this._spawnSphere(x, y, z, spec.color, 0.8, 0.18);
    this.spawnBurst(x, y, z, 8, spec.color, 0.35);
  }

  spawnExplosion(x: number, y: number, z: number, scale = 1.0): void {
    // 1. Instant white flash pop
    this._spawnSphere(x, y + 0.5, z, 0xffffff, 7 * scale, 0.12);
    // 2. Orange fireball core
    this._spawnSphere(x, y + 0.5, z, 0xff5500, 5 * scale, 0.50);
    // 3. Shockwave ring — expands outward fast
    this._spawnRing(x, y + 0.3, z, 0xff8800, 10 * scale, 0.35);
    // 4. Secondary wider ring (white)
    this._spawnRing(x, y + 0.3, z, 0xffffff, 7 * scale, 0.22);
    // 5. Heavy debris burst
    this.spawnBurst(x, y, z, Math.round(80 * scale), 0xfb923c, 2.0 * scale);
    this.spawnBurst(x, y, z, Math.round(40 * scale), 0xf472b6, 1.5 * scale);
  }

  spawnMuzzleFlash(x: number, y: number, z: number, dirX: number, dirZ: number, weaponType = ""): void {
    const color = MUZZLE_COLORS[weaponType] ?? MUZZLE_COLORS["standard-plasma"];
    const mat  = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 1.0 });
    const mesh = new THREE.Mesh(_MUZZLE_GEO, mat);
    // Push slightly forward of barrel tip; elongate toward fire direction
    const ox = x + dirX * 0.5;
    const oz = z + dirZ * 0.5;
    mesh.position.set(ox, y, oz);
    mesh.scale.set(1.4, 1.0, 1.4);
    this.group.add(mesh);
    this.muzzleFlashes.push({ mesh, ttl: 0.10, initialTtl: 0.10 });
    // Ring at muzzle tip
    this._spawnRing(ox, y, oz, color, 1.8, 0.07);
  }

  update(dt: number): void {
    // ── Muzzle flashes ────────────────────────────────────────────────────────
    let fi = 0;
    while (fi < this.muzzleFlashes.length) {
      const m = this.muzzleFlashes[fi];
      m.ttl -= dt;
      if (m.ttl <= 0) {
        this.group.remove(m.mesh);
        (m.mesh.material as THREE.Material).dispose();
        const last = this.muzzleFlashes.length - 1;
        if (fi < last) this.muzzleFlashes[fi] = this.muzzleFlashes[last];
        this.muzzleFlashes.length--;
      } else {
        const t = m.ttl / m.initialTtl;
        const mat = m.mesh.material as THREE.MeshBasicMaterial;
        mat.opacity = t;
        // Expand outward as it fades
        const sc = 1.4 + (1 - t) * 2.0;
        m.mesh.scale.set(sc, sc * 0.7, sc);
        fi++;
      }
    }

    // ── Fireball spheres ──────────────────────────────────────────────────────
    let si = 0;
    while (si < this.spheres.length) {
      const s = this.spheres[si];
      s.ttl -= dt;
      if (s.ttl <= 0) {
        this.group.remove(s.mesh);
        s.mat.dispose();
        const last = this.spheres.length - 1;
        if (si < last) this.spheres[si] = this.spheres[last];
        this.spheres.length--;
      } else {
        const t = 1 - s.ttl / s.initialTtl; // 0→1 over lifetime
        // Quick expand (sin curve), fade out in second half
        const sc = s.targetScale * Math.sin(t * Math.PI * 0.9);
        s.mesh.scale.setScalar(Math.max(0.05, sc));
        s.mat.opacity = t < 0.3 ? 1.0 : Math.pow(s.ttl / s.initialTtl, 0.6);
        si++;
      }
    }

    // ── Shockwave rings ───────────────────────────────────────────────────────
    let ri = 0;
    while (ri < this.rings.length) {
      const r = this.rings[ri];
      r.ttl -= dt;
      if (r.ttl <= 0) {
        this.group.remove(r.mesh);
        r.mat.dispose();
        const last = this.rings.length - 1;
        if (ri < last) this.rings[ri] = this.rings[last];
        this.rings.length--;
      } else {
        const t = 1 - r.ttl / r.initialTtl;
        // Linear expand outward
        const sc = 0.05 + t * r.targetScale;
        r.mesh.scale.setScalar(sc);
        // Fade out as it expands
        r.mat.opacity = Math.pow(r.ttl / r.initialTtl, 1.8) * 0.9;
        ri++;
      }
    }

    // ── Bursts ────────────────────────────────────────────────────────────────
    const drag = Math.pow(0.92, dt * 60);
    let bi = 0;
    while (bi < this.bursts.length) {
      const b = this.bursts[bi];
      b.ttl -= dt;
      if (b.ttl <= 0) {
        for (const piece of b.pieces) {
          piece.mesh.material = _PROJ_MAT;
          this.burstMeshPool.push(piece.mesh);
        }
        b.group.clear();
        this.group.remove(b.group);
        b.mat.dispose();
        const last = this.bursts.length - 1;
        if (bi < last) this.bursts[bi] = this.bursts[last];
        this.bursts.length--;
      } else {
        const alpha = b.ttl / b.initialTtl;
        b.mat.opacity = alpha;
        const scale = 0.4 + alpha * 0.8;
        for (const piece of b.pieces) {
          piece.mesh.position.x += piece.vx * dt;
          piece.mesh.position.y += piece.vy * dt;
          piece.mesh.position.z += piece.vz * dt;
          piece.vy -= 8 * dt;
          piece.vx *= drag;
          piece.vz *= drag;
          piece.mesh.rotation.x += piece.rvx * dt;
          piece.mesh.rotation.y += piece.rvy * dt;
          piece.mesh.rotation.z += piece.rvz * dt;
          piece.mesh.scale.setScalar(scale);
        }
        bi++;
      }
    }

    // ── Rocket smoke puffs (pooled) ───────────────────────────────────────────
    let pi = 0;
    while (pi < this.smokePuffs.length) {
      const puff = this.smokePuffs[pi];
      puff.ttl -= dt;
      if (puff.ttl <= 0) {
        this.group.remove(puff.mesh);
        this.smokePool.push({ mesh: puff.mesh, mat: puff.mat });
        const last = this.smokePuffs.length - 1;
        if (pi < last) this.smokePuffs[pi] = this.smokePuffs[last];
        this.smokePuffs.length--;
      } else {
        const t = puff.ttl / puff.initialTtl;
        puff.mat.opacity = 0.5 * t;
        puff.mesh.scale.setScalar(0.6 + (1 - t) * 1.4); // billow out as it fades
        puff.mesh.position.y += dt * 0.8;               // drift upward
        pi++;
      }
    }

    // ── Projectile TTL countdown ──────────────────────────────────────────────
    for (const p of this.projectiles.values()) {
      p.ttl -= dt;
      if (p.puffTimer > 0) p.puffTimer -= dt;
    }
  }

  private spawnBurst(x: number, y: number, z: number, count: number, color: number, lifetime: number): void {
    const group = new THREE.Group();
    const mat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 1 });
    const pieces: BurstPiece[] = [];
    for (let i = 0; i < count; i++) {
      const m = this.burstMeshPool.pop() ?? new THREE.Mesh(_BURST_GEO);
      m.material = mat;
      m.position.set(x, y, z);
      const phi   = Math.random() * Math.PI * 2;
      const theta = Math.random() * Math.PI;
      const speed = 6 + Math.random() * 14;
      group.add(m);
      pieces.push({
        mesh: m,
        vx:  Math.sin(theta) * Math.cos(phi) * speed,
        vy:  Math.abs(Math.cos(theta)) * speed + speed * 0.4,
        vz:  Math.sin(theta) * Math.sin(phi) * speed,
        rvx: (Math.random() - 0.5) * 14,
        rvy: (Math.random() - 0.5) * 14,
        rvz: (Math.random() - 0.5) * 14,
      });
    }
    this.group.add(group);
    this.bursts.push({ group, ttl: lifetime, initialTtl: lifetime, mat, pieces });
  }

  private _spawnSphere(x: number, y: number, z: number, color: number, targetScale: number, lifetime: number): void {
    const mat  = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 1.0 });
    const mesh = new THREE.Mesh(_FIREBALL_GEO, mat);
    mesh.position.set(x, y, z);
    mesh.scale.setScalar(0.05);
    this.group.add(mesh);
    this.spheres.push({ mesh, mat, ttl: lifetime, initialTtl: lifetime, targetScale });
  }

  private _spawnRing(x: number, y: number, z: number, color: number, targetScale: number, lifetime: number): void {
    const mat  = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.9, side: THREE.DoubleSide });
    const mesh = new THREE.Mesh(_RING_GEO, mat);
    mesh.rotation.x = -Math.PI / 2; // lay flat horizontally
    mesh.position.set(x, y, z);
    mesh.scale.setScalar(0.05);
    this.group.add(mesh);
    this.rings.push({ mesh, mat, ttl: lifetime, initialTtl: lifetime, targetScale });
  }

  knownProjectiles(): IterableIterator<string> {
    return this.projectiles.keys();
  }

  get activeBursts(): number {
    return this.bursts.length;
  }
}
